// src/components/jobs/MyApplications.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Briefcase, Clock, DollarSign, Eye, Filter, 
  Search, AlertCircle, CheckCircle, XCircle, 
  TrendingUp, FileText, RefreshCw
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { jobService } from '../../services/jobService';

const MyApplications = () => {
  const { userProfile, initialLoading } = useAuth();
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  
  const [filters, setFilters] = useState({
    search: '',
    status: 'all'
  });

  const statusOptions = [
    { value: 'all', label: 'All Applications' },
    { value: 'pending', label: 'Pending Review' },
    { value: 'reviewed', label: 'Reviewed' },
    { value: 'shortlisted', label: 'Shortlisted' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'withdrawn', label: 'Withdrawn' }
  ];

  useEffect(() => {
    if (!initialLoading && userProfile?.uid) {
      loadApplications();
    }
  }, [userProfile, initialLoading]);

  useEffect(() => {
    applyFilters();
  }, [applications, filters]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Loading applications for user:', userProfile.uid);

      const [applicationsData, statsData] = await Promise.all([
        jobService.getApplicationsByVA(userProfile.uid),
        jobService.getVAApplicationStats(userProfile.uid)
      ]);

      setApplications(applicationsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading applications:', error);
      setError('Failed to load applications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...applications];

    if (filters.status !== 'all') {
      filtered = filtered.filter(app => app.status === filters.status);
    }

    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(app =>
        app.jobTitle?.toLowerCase().includes(searchLower) ||
        app.coverLetter?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredApplications(filtered);
  };

  const handleWithdraw = async (applicationId) => {
    if (!window.confirm('Are you sure you want to withdraw this application?')) {
      return;
    }

    try {
      await jobService.withdrawApplication(applicationId, userProfile.uid);
      loadApplications();
    } catch (error) {
      alert('Failed to withdraw application. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200', label: 'Pending' },
      reviewed: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', label: 'Reviewed' },
      shortlisted: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200', label: 'Shortlisted' },
      accepted: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', label: 'Accepted' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', label: 'Rejected' },
      withdrawn: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200', label: 'Withdrawn' }
    };

    const style = config[status] || config.pending;

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${style.bg} ${style.text} ${style.border}`}>
        {style.label}
      </span>
    );
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays <= 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-ZA');
  };

  // Show loading while auth initializes
  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error if no user profile
  if (!userProfile?.uid) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="mx-auto h-16 w-16 text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Profile Not Found</h3>
          <p className="text-gray-600 mb-4">Unable to load your profile. Please try logging in again.</p>
          <Link to="/login" className="text-blue-600 hover:text-blue-700">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Applications</h1>
          <p className="text-gray-600">Track and manage your job applications</p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <Briefcase size={20} className="text-gray-400 mb-2" />
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-xs text-gray-600">Total</p>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <Clock size={20} className="text-yellow-600 mb-2" />
              <p className="text-2xl font-bold text-yellow-900">{stats.pending}</p>
              <p className="text-xs text-yellow-700">Pending</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <Eye size={20} className="text-blue-600 mb-2" />
              <p className="text-2xl font-bold text-blue-900">{stats.reviewed}</p>
              <p className="text-xs text-blue-700">Reviewed</p>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <TrendingUp size={20} className="text-purple-600 mb-2" />
              <p className="text-2xl font-bold text-purple-900">{stats.shortlisted}</p>
              <p className="text-xs text-purple-700">Shortlisted</p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <CheckCircle size={20} className="text-green-600 mb-2" />
              <p className="text-2xl font-bold text-green-900">{stats.accepted}</p>
              <p className="text-xs text-green-700">Accepted</p>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <XCircle size={20} className="text-red-600 mb-2" />
              <p className="text-2xl font-bold text-red-900">{stats.rejected}</p>
              <p className="text-xs text-red-700">Rejected</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
              <TrendingUp size={20} className="text-blue-600 mb-2" />
              <p className="text-2xl font-bold text-blue-900">{stats.responseRate.toFixed(0)}%</p>
              <p className="text-xs text-blue-700">Response</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder="Search applications..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="sm:w-48">
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={loadApplications}
              className="sm:w-auto bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors flex items-center justify-center"
            >
              <RefreshCw size={18} className="mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start">
            <AlertCircle className="text-red-600 flex-shrink-0 mr-3 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-red-900">Error</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {filteredApplications.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
            <Briefcase size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filters.status !== 'all' || filters.search ? 'No matching applications' : 'No applications yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {filters.status !== 'all' || filters.search 
                ? 'Try adjusting your filters'
                : 'Start by browsing available jobs and submitting your first application'
              }
            </p>
            {!filters.search && filters.status === 'all' && (
              <Link
                to="/jobs/browse"
                className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                <Search className="mr-2" size={18} />
                Browse Jobs
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApplications.map((app) => (
              <div key={app.id} className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <Link 
                          to={`/jobs/${app.jobId}`}
                          className="text-xl font-semibold text-gray-900 hover:text-blue-600 transition-colors"
                        >
                          {app.jobTitle}
                        </Link>
                        <p className="text-sm text-gray-600 mt-1">
                          Applied {getTimeAgo(app.createdAt)}
                        </p>
                      </div>
                      {getStatusBadge(app.status)}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Your Rate</p>
                        <p className="font-semibold text-gray-900">
                          R{app.proposedRate?.toLocaleString()}
                          {app.rateType === 'hourly' && '/hr'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Job Budget</p>
                        <p className="font-semibold text-gray-900">R{app.jobBudget?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Timeframe</p>
                        <p className="font-semibold text-gray-900">{app.proposedTimeframe}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Start Date</p>
                        <p className="font-semibold text-gray-900">
                          {app.startDate ? new Date(app.startDate).toLocaleDateString() : 'Flexible'}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4">
                      <p className="text-sm text-gray-700 line-clamp-2">
                        {app.coverLetter}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    {app.viewedByClient && (
                      <div className="flex items-center text-blue-600">
                        <Eye size={14} className="mr-1" />
                        <span>Viewed</span>
                      </div>
                    )}
                    {app.attachments?.length > 0 && (
                      <div className="flex items-center">
                        <FileText size={14} className="mr-1" />
                        <span>{app.attachments.length} file{app.attachments.length !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-3">
                    <Link
                      to={`/jobs/${app.jobId}`}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                    >
                      View Job
                    </Link>
                    
                    {app.status === 'pending' && (
                      <button
                        onClick={() => handleWithdraw(app.id)}
                        className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
                      >
                        Withdraw
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyApplications;